import React, { CSSProperties, useState } from 'react'
import { FormattedHTMLMessage, Button, Tooltip, Modal, useBreakpoints, newIcons, AppSvg } from '@web/walter-ui-kit'

import { GetProducts } from '@web/walter-lib/api'
import cn from 'classnames'
import s from './s.module.css'
import { lang } from './lang'
import { Fee } from './Fee'
import CardImage, { getCardType } from '~pages/CardsPage/components/CardImage'
import ExternalLink from '~components/ExternalLink'

interface Props {
    data?: GetProducts
    onButtonClick?: (data: GetProducts) => void
    isModal?: boolean
    showOffer?: boolean
    shortTexts?: boolean
    lineClampCount?: number
    type?: 'primary' | 'secondary' | 'tertiary' | 'quadro'
    onClose?: () => void
}

// eslint-disable-next-line complexity,max-lines-per-function
function Card({
    data,
    onButtonClick,
    isModal,
    showOffer = true,
    type = 'primary',
    onClose,
    shortTexts,
    lineClampCount,
}: Props) {
    const { isMobile, isTablet } = useBreakpoints()
    const [isOpen, setIsOpen] = useState(false)

    const cssVariables = {
        '--line-clamp-count': lineClampCount,
    } as CSSProperties

    return (
        <>
            <CardModal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                data={data}
                onButtonClick={data => {
                    data && onButtonClick?.(data)
                    setIsOpen(false)
                }}
                type="quadro"
                showOffer
            />
            <div
                className={cn(s.wrap, isModal && s.mobileWrap, type === 'secondary' && s.secondaryWrap)}
                style={cssVariables}
                data-pointer={`new_card_${data?.id}`}
            >
                <CardImage
                    className={cn(
                        s.img,
                        isModal && s.modalImg,
                        (type === 'primary' || type === 'tertiary' || type === 'quadro') && s.hide
                    )}
                    designId={data?.design?.id}
                    currency={isMobile || isTablet ? undefined : data?.currency}
                    cardType={isMobile || isTablet ? undefined : data?.card_type}
                />
                <div className={s.left}>
                    {(type === 'tertiary' || type === 'quadro') && (
                        <div className={s.badges}>
                            {data?.card_type && <div className={s.badge}>{getCardType(data?.card_type)}</div>}
                            {data?.currency && <div className={s.badge}>{data?.currency?.toUpperCase?.()}</div>}
                        </div>
                    )}
                    {data && (
                        <div
                            className={cn((type === 'tertiary' || shortTexts) && s.tertiaryMainContent, s.mainContent)}
                        >
                            <Tooltip
                                wrapperClassName={s.tooltip}
                                dataPointer="title_tooltip"
                                content={<span className={s.tooltipText}>{data.name}</span>}
                                isDisabled={!data.name || (type !== 'tertiary' && !shortTexts)}
                            >
                                <FormattedHTMLMessage
                                    id={`product.name.${data.id}`}
                                    defaultMessage={data.name || ''}
                                    className={s.title}
                                />
                            </Tooltip>
                            <Tooltip
                                dataPointer="description_tooltip"
                                content={<span className={s.tooltipText}>{data.description}</span>}
                                isDisabled={!data.description || (type !== 'tertiary' && !shortTexts)}
                                wrapperClassName={s.tooltip}
                            >
                                <FormattedHTMLMessage
                                    id={`product.description.${data.id}`}
                                    defaultMessage={data.description || ''}
                                    className={s.description}
                                />
                            </Tooltip>
                            {showOffer && type !== 'tertiary' && (
                                <ExternalLink href={`/cards/${data?.id}/agreements/${data?.agreement_id}`}>
                                    <FormattedHTMLMessage {...lang.agreement} className={s.agreement} />
                                </ExternalLink>
                            )}
                        </div>
                    )}
                    <div className={s.divider} />
                    <Fee data={data} type={type} />
                    {data?.additional_info && type !== 'tertiary' && (
                        <>
                            <div className={s.divider} />
                            <div className={cn(s.addInfo, shortTexts && s.addInfoShorten)}>
                                <FormattedHTMLMessage {...lang.addInfo} className={s.addInfoTitle} />
                                <Tooltip
                                    dataPointer="additional_info_tooltip"
                                    content={<span className={s.tooltipText}>{data.additional_info}</span>}
                                    isDisabled={!data.description || !shortTexts}
                                    wrapperClassName={s.tooltip}
                                >
                                    <FormattedHTMLMessage
                                        id={`product.additional_info.${data.id}`}
                                        defaultMessage={data.additional_info || ''}
                                        className={s.addInfoText}
                                    />
                                </Tooltip>
                            </div>
                        </>
                    )}
                    {type === 'tertiary' && <div className={s.divider} />}
                    {type !== 'quadro' && onButtonClick && data && (
                        <div className={cn(s.button, type === 'tertiary' && s.tertiaryButton)}>
                            {type === 'tertiary' && (
                                <Button
                                    onClick={() => setIsOpen(true)}
                                    mode="secondary"
                                    size="m"
                                    dataPointer="details_button"
                                    fullWidth={isMobile || isTablet}
                                >
                                    <FormattedHTMLMessage {...lang.details} />
                                </Button>
                            )}
                            <Button
                                onClick={() => onButtonClick(data)}
                                mode="primary"
                                size="m"
                                dataPointer="choose_card_button"
                                suffix={<AppSvg svgName={newIcons.ic24ArrowRight} size={24} path="new-icons" />}
                                fullWidth={isMobile || isTablet}
                            >
                                <FormattedHTMLMessage {...lang.button} />
                            </Button>
                        </div>
                    )}
                    {type === 'quadro' && (
                        <div className={cn(s.button, s.quadroButton)}>
                            <Button
                                onClick={() => onClose?.()}
                                mode="secondary"
                                size="m"
                                dataPointer="close_this_card_button"
                                fullWidth
                            >
                                <FormattedHTMLMessage {...lang.close} />
                            </Button>
                            <Button
                                onClick={() => data && onButtonClick?.(data)}
                                mode="primary"
                                size="m"
                                dataPointer="choose_this_card_button"
                                suffix={<AppSvg svgName={newIcons.ic24ArrowRight} size={24} path="new-icons" />}
                                fullWidth
                            >
                                <FormattedHTMLMessage {...lang.choose} />
                            </Button>
                        </div>
                    )}
                </div>
                {type !== 'quadro' && (
                    <div className={cn(type === 'tertiary' && s.tertiaryRight, type === 'secondary' && s.hide)}>
                        <CardImage
                            className={cn(s.img, s.rightImage, isModal && s.modalImg)}
                            designId={data?.design?.id}
                            currency={isMobile || isTablet ? undefined : data?.currency}
                            cardType={isMobile || isTablet ? undefined : data?.card_type}
                        />
                    </div>
                )}
            </div>
        </>
    )
}

interface ModalProps extends Props {
    isOpen: boolean
    onClose: () => void
}

function CardModal({ isOpen, onClose, ...rest }: ModalProps) {
    return (
        <Modal
            type="dialog"
            size="xs"
            withHeaderBorder={false}
            onClose={onClose}
            isOpen={isOpen}
            dataPointer="card_modal"
            withModalScroll
        >
            <Card isModal {...rest} onClose={onClose} />
        </Modal>
    )
}

export { Card, CardModal }
